import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SCENARIOS = {
  approval_needed: {
    title: "claude-code",
    subtitle: "refactor-auth-module",
    lines: [
      { text: "$ claude-code --task 'refactor auth module'", type: "command", delay: 0 },
      { text: "Scanning project structure...", type: "info", delay: 800 },
      { text: "Found 14 files in /src/auth/", type: "info", delay: 1600 },
      { text: "Analyzing dependencies...", type: "info", delay: 2400 },
      { text: "Refactoring src/auth/middleware.ts", type: "info", delay: 3200 },
      { text: "  + Added token refresh logic", type: "success", delay: 4000 },
      { text: "  + Updated session validation", type: "success", delay: 4600 },
      { text: "Refactoring src/auth/providers.ts", type: "info", delay: 5200 },
      { text: "  + Migrated to OAuth 2.1", type: "success", delay: 5800 },
      { text: "", type: "blank", delay: 6400 },
      { text: "ACTION REQUIRED", type: "warning", delay: 6600 },
      { text: "Agent wants to execute: npm install @auth/core@latest", type: "warning", delay: 7000 },
      { text: "This will modify package.json and package-lock.json", type: "muted", delay: 7400 },
      { text: "Waiting for approval...", type: "waiting", delay: 7800 },
    ],
    resumeLines: [
      { text: "Approved. Continuing...", type: "success", delay: 200 },
      { text: "$ npm install @auth/core@latest", type: "command", delay: 600 },
      { text: "added 3 packages in 2.1s", type: "info", delay: 1400 },
      { text: "Running tests...", type: "info", delay: 2000 },
      { text: "  PASS src/auth/middleware.test.ts", type: "success", delay: 2800 },
      { text: "  PASS src/auth/providers.test.ts", type: "success", delay: 3200 },
      { text: "  PASS src/auth/session.test.ts", type: "success", delay: 3600 },
      { text: "", type: "blank", delay: 4000 },
      { text: "All tests passing. 14 files updated.", type: "success", delay: 4200 },
      { text: "Session complete.", type: "complete", delay: 4800 },
    ],
    notification: {
      title: "Runzr",
      body: "Agent needs approval to run npm install @auth/core@latest",
      badge: "Needs Input",
    },
    actions: ["Approve", "Deny"],
  },
  tests_failed: {
    title: "codex-cli",
    subtitle: "fix-payments-api",
    lines: [
      { text: "$ codex --task 'fix payment processing bug'", type: "command", delay: 0 },
      { text: "Analyzing payments service...", type: "info", delay: 800 },
      { text: "Found bug in src/payments/processor.ts:142", type: "info", delay: 1600 },
      { text: "Applying fix...", type: "info", delay: 2400 },
      { text: "  ~ Modified currency conversion logic", type: "info", delay: 3000 },
      { text: "  ~ Updated rounding to 2 decimal places", type: "info", delay: 3600 },
      { text: "Running test suite...", type: "info", delay: 4200 },
      { text: "", type: "blank", delay: 4800 },
      { text: "  PASS src/payments/processor.test.ts", type: "success", delay: 5200 },
      { text: "  FAIL src/payments/webhook.test.ts", type: "error", delay: 5800 },
      { text: "    Expected: 200", type: "error", delay: 6200 },
      { text: "    Received: 422", type: "error", delay: 6400 },
      { text: "  FAIL src/payments/refund.test.ts", type: "error", delay: 6800 },
      { text: "", type: "blank", delay: 7200 },
      { text: "2 of 3 tests failed. Session halted.", type: "error", delay: 7400 },
    ],
    resumeLines: [
      { text: "Debugging with AI...", type: "info", delay: 200 },
      { text: "Analyzing test failures...", type: "info", delay: 800 },
      { text: "Root cause: webhook signature mismatch after refactor", type: "info", delay: 1600 },
      { text: "Applying fix to src/payments/webhook.ts", type: "info", delay: 2400 },
      { text: "  + Updated HMAC signature validation", type: "success", delay: 3000 },
      { text: "Re-running tests...", type: "info", delay: 3600 },
      { text: "  PASS src/payments/processor.test.ts", type: "success", delay: 4200 },
      { text: "  PASS src/payments/webhook.test.ts", type: "success", delay: 4600 },
      { text: "  PASS src/payments/refund.test.ts", type: "success", delay: 5000 },
      { text: "", type: "blank", delay: 5400 },
      { text: "All tests passing. Fix applied.", type: "success", delay: 5600 },
    ],
    notification: {
      title: "Runzr",
      body: "Test suite failed: 2 of 3 tests in payments service",
      badge: "Failed",
    },
    actions: ["Debug with AI", "Abort"],
  },
  task_completed: {
    title: "claude-code",
    subtitle: "migrate-database-schema",
    lines: [
      { text: "$ claude-code --task 'migrate to new database schema'", type: "command", delay: 0 },
      { text: "Scanning current schema...", type: "info", delay: 800 },
      { text: "Planning migration for 8 tables...", type: "info", delay: 1600 },
      { text: "Generating migration files...", type: "info", delay: 2400 },
      { text: "  + Created 001_add_user_preferences.sql", type: "success", delay: 3000 },
      { text: "  + Created 002_update_orders_table.sql", type: "success", delay: 3400 },
      { text: "  + Created 003_add_analytics_index.sql", type: "success", delay: 3800 },
      { text: "Applying migrations...", type: "info", delay: 4400 },
      { text: "  Applied 001_add_user_preferences.sql", type: "success", delay: 5000 },
      { text: "  Applied 002_update_orders_table.sql", type: "success", delay: 5600 },
      { text: "  Applied 003_add_analytics_index.sql", type: "success", delay: 6200 },
      { text: "Running validation...", type: "info", delay: 6800 },
      { text: "  All constraints valid", type: "success", delay: 7400 },
      { text: "  All indexes verified", type: "success", delay: 7800 },
      { text: "", type: "blank", delay: 8200 },
      { text: "Migration complete. 8 tables updated, 3 migrations applied.", type: "complete", delay: 8400 },
    ],
    resumeLines: [],
    notification: {
      title: "Runzr",
      body: "Session complete: 8 tables updated, all migrations applied successfully",
      badge: "Complete",
    },
    actions: ["View Summary", "Open PR"],
  },
};

function TerminalLine({ line }) {
  const colorMap = {
    command: "#fff",
    info: "#A1A1AA",
    success: "#10B981",
    error: "#EF4444",
    warning: "#F59E0B",
    waiting: "#F59E0B",
    muted: "#71717A",
    complete: "#10B981",
    blank: "transparent",
  };

  const prefix =
    line.type === "warning"
      ? "⚠ "
      : line.type === "error"
      ? "✗ "
      : line.type === "success" && !line.text.startsWith("$")
      ? "✓ "
      : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="leading-relaxed"
      style={{
        color: colorMap[line.type] || "#A1A1AA",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "12px",
        minHeight: line.type === "blank" ? "16px" : "auto",
      }}
    >
      {prefix}
      {line.text}
      {line.type === "waiting" && <span className="cursor-blink ml-1">█</span>}
    </motion.div>
  );
}

export default function TerminalMockup({ scenario = "approval_needed", onNeedInput, onComplete, isResumed, className = "" }) {
  const [visibleLines, setVisibleLines] = useState([]);
  const [phase, setPhase] = useState("initial");
  const terminalRef = useRef(null);
  const timeoutsRef = useRef([]);

  const data = SCENARIOS[scenario];

  useEffect(() => {
    setVisibleLines([]);
    setPhase("initial");
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    data.lines.forEach((line, i) => {
      const t = setTimeout(() => {
        setVisibleLines((prev) => [...prev, line]);
        if (i === data.lines.length - 1) {
          setPhase("waiting");
          onNeedInput?.();
        }
      }, line.delay);
      timeoutsRef.current.push(t);
    });

    return () => timeoutsRef.current.forEach(clearTimeout);
  }, [data.lines, onNeedInput, scenario]);

  useEffect(() => {
    if (isResumed && phase === "waiting" && data.resumeLines.length > 0) {
      setPhase("resumed");
      data.resumeLines.forEach((line, i) => {
        const t = setTimeout(() => {
          setVisibleLines((prev) => [...prev, line]);
          if (i === data.resumeLines.length - 1) {
            onComplete?.();
          }
        }, line.delay);
        timeoutsRef.current.push(t);
      });
    }
  }, [data.resumeLines, isResumed, onComplete, phase]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [visibleLines]);

  const badgeColor =
    phase === "waiting"
      ? scenario === "tests_failed"
        ? "#EF4444"
        : "#F59E0B"
      : phase === "resumed" || (scenario === "task_completed" && phase === "waiting")
      ? "#10B981"
      : "#F59E0B";

  const badgeText =
    phase === "waiting"
      ? scenario === "task_completed"
        ? "Complete"
        : scenario === "tests_failed"
        ? "Failed"
        : "Needs Input"
      : phase === "resumed"
      ? "Running"
      : "Running";

  return (
    <div className={`relative ${className}`}>
      {/* macOS window */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: "#0D0D0D",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
        }}
      >
        {/* Title bar */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: "#FF5F56" }} />
            <div className="w-3 h-3 rounded-full" style={{ background: "#FFBD2E" }} />
            <div className="w-3 h-3 rounded-full" style={{ background: "#27C93F" }} />
          </div>
          <div
            className="text-xs"
            style={{ color: "#71717A", fontFamily: "'JetBrains Mono', monospace" }}
          >
            {data.title} — {data.subtitle}
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{
                border: `1px solid ${badgeColor}`,
                color: badgeColor,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {badgeText}
            </span>
          </div>
        </div>
        {/* Terminal body */}
        <div
          ref={terminalRef}
          className="p-4 overflow-y-auto"
          style={{ height: "320px" }}
          data-testid="terminal-output"
        >
          <AnimatePresence>
            {visibleLines.map((line, i) => (
              <TerminalLine key={`${scenario}-${i}`} line={line} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export { SCENARIOS };
